/**
 * PREFERENCES — the port's own settings panel, and the one screen that holds
 * every knob this game has.
 *
 * ## Why it looks like the Realism Panel
 *
 * Because it is the same construction. `realism-screen.ts` recovered the
 * original's panel vocabulary from `SIMREAL.BOX` and `realism-panel.png` — a
 * raised outer plate, inner plates for groups, an 11×11 sunken checkbox beside
 * a chip carrying its label, a chip-and-well pair for a value that cycles, and
 * a row of 90×18 buttons along the bottom. Every rect below is that vocabulary
 * at this screen's own measurements. The original has no PREFERENCES screen to
 * copy (its `PREFERENCES` entry under `OTHER OPTIONS` configured sound cards
 * and input devices a browser has no business asking about), so this is a port
 * screen — but it is a port screen drawn in the game's hand rather than in a
 * second visual language bolted to the side of it.
 *
 * ## Why one screen instead of a row of chips
 *
 * The settings it consolidates were seven HTML chips in the corner of the
 * flight view plus an audio popover, and `app.css` had reached the point of
 * telling the next lane that a ninth chip had to *replace* one. A row of chips
 * is a flat namespace with no room for sections, no room for a value that is
 * not a boolean, and no way to say which knob belongs with which. This panel
 * has three named groups — DISPLAY (the port extensions), AUDIO (the mixer)
 * and FLYING (the two input/instrument choices) — and a button through to the
 * Realism Panel, which is the original's own screen and stays exactly as it
 * was measured.
 */

import {
  EFFECT_TOGGLE_DEPENDS_ON,
  EFFECT_TOGGLE_IDS,
  EFFECT_TOGGLE_LABELS,
  EFFECT_TOGGLE_UNAVAILABLE_LABELS,
  type EffectToggleId,
  VOLUME_BUS_LABELS,
  VOLUME_BUSES,
  volumePercent,
  type Preferences,
} from "../../mission/preferences.ts";
import { isRendererFallback, rendererFactLabel, type RendererFacts } from "../../three/renderer-mode.ts";
import type { Rect } from "../render/draw-ops.ts";
import type { MenuAction } from "./menu-model.ts";
import type { ShellScreen, Widget } from "./shell-screen.ts";

const command = (name: string): MenuAction => ({ kind: "command", command: name });

/** The same outer plate the Realism Panel sits on, so the two screens are the same object at a glance. */
export const PREFERENCES_OUTER_PLATE: Rect = { x: 6, y: 27, w: 307, h: 163 };
export const DISPLAY_PLATE: Rect = { x: 13, y: 36, w: 145, h: 120 };
/**
 * The right column, sized to its **content** rather than to a round number.
 *
 * FLYING needs four rows, and four 11-tall rows plus an 8-pixel heading and the
 * plate's own 3-pixel margins is 58 pixels — which, with the button row fixed
 * at y 161, puts the plate's top at 102 and leaves AUDIO exactly 66. AUDIO in
 * turn needs 3 + 8 for its heading, 39 for its three 13-tall volume chips and 13
 * for MUTE, plus 3: 66 to the pixel. So the two plates end and begin on the same
 * line at y 102, which is tight rather than accidental — there is no arrangement
 * of this content in this column that leaves a gap.
 *
 * `preferences-screen.test.ts` measures every one of those rows against the
 * plate that carries it, which is the assertion `lessons.md`'s *"a screen sized
 * to its chrome is not sized to its content"* entry exists to ask for.
 */
export const AUDIO_PLATE: Rect = { x: 163, y: 36, w: 144, h: 66 };
export const FLYING_PLATE: Rect = { x: 163, y: 102, w: 144, h: 58 };

/**
 * The action row, at the width **four** buttons fit in.
 *
 * It was three 90-wide buttons on the Realism Panel's own measurements, and a
 * joystick needs a screen of its own for the same reason the Realism Panel does
 * (`controls-screen.ts`). Four 71-wide buttons span the same 13…306 the three
 * did, and the widest label any of them carries is `CALIBRATE` at 50 px of
 * `FONT1` — so the row is tighter than it was and still nowhere near full.
 */
const BUTTON_WIDTH = 71;
export const REALISM_BUTTON_RECT: Rect = { x: 13, y: 161, w: BUTTON_WIDTH, h: 18 };
export const CONTROLS_BUTTON_RECT: Rect = { x: 87, y: 161, w: BUTTON_WIDTH, h: 18 };
export const RESTORE_BUTTON_RECT: Rect = { x: 161, y: 161, w: BUTTON_WIDTH, h: 18 };
export const ACCEPT_BUTTON_RECT: Rect = { x: 235, y: 161, w: BUTTON_WIDTH, h: 18 };

/**
 * DISPLAY's seven checkbox rows, on an even 15-pixel pitch.
 *
 * Every port extension in this section is a plain on/off
 * (`effects-settings.ts`), so the section is one uniform list rather than a list
 * with a differently-shaped control at the bottom of it — which is what lets
 * `displaySection` map `EFFECT_TOGGLE_IDS` straight onto these rows with nothing
 * appended.
 *
 * The seventh (3D COCKPIT) is the last one this plate holds: its chip runs
 * y 139-152 and the plate's inside ends at 154, so an eighth row would need the
 * plate to grow into the REALISM button at y 161. The next setting that wants a
 * home here has to move the buttons or split the section, and that is worth
 * knowing before it is discovered by a row landing on top of a button.
 */
const DISPLAY_ROWS: readonly number[] = [50, 65, 80, 95, 110, 125, 140];
/** AUDIO's three volume rows, then MUTE — a 13-pixel pitch at 13 tall, which is the tightest a 13-tall chip stacks. */
const VOLUME_ROWS: readonly number[] = [47, 60, 73];
/** MUTE's chip is drawn a pixel above its checkbox, so 87 puts its top edge flush on the third volume row's bottom. */
const MUTE_ROW = 87;
/**
 * FLYING's four rows, on an 11-pixel pitch at 11 tall — the tightest an
 * 11-pixel checkbox stacks, and what a fourth row costs.
 *
 * The section holds AD-007n's stick shaping, REALISTIC INSTRUMENTS, AD-021's
 * BAIL OUT and I12's ENHANCED SIMULATION. The plate's inside runs 105–157 and
 * the button row starts at 161, so the last row ends on the plate's own inside
 * edge with four pixels to spare before RESTORE. A **fifth** row cannot be had
 * here at any pitch: it would have to come out of AUDIO, which is already at
 * its own content's exact height.
 */
const FLYING_ROW_HEIGHT = 11;
const STICK_ROW = 113;
const INSTRUMENTS_ROW = 124;
const BAIL_ROW = 135;
const SIMULATION_ROW = 146;

/** The checkbox interior plus its two-pixel sunken frame, as `realism-screen.ts` measures it. */
function checkbox(x: number, y: number): Rect {
  return { x, y, w: 11, h: 11 };
}

function sectionHeading(plate: Rect, text: string): Widget {
  return { kind: "text", text, x: plate.x + 4, y: plate.y + 3, role: "prompt" };
}

/**
 * Where the renderer fact is printed: DISPLAY's heading row, to the right of the
 * word DISPLAY.
 *
 * **A fixed column rather than a right alignment**, because this module is pure
 * and has no font atlas to measure with — every other position on this screen is
 * a measured constant for the same reason. The column is chosen so the *widest*
 * string the line can hold ends on the plate's own inside edge: `RENDERER:
 * WEBGPU` is 78 px of `FONT3` and DISPLAY_PLATE's inside right edge is x 154, so
 * 154 − 78 = 76. `DISPLAY` itself ends at x 52, leaving 24 px of gap.
 *
 * Those three numbers are asserted against the real baked atlas in
 * `preferences-screen.test.ts` rather than trusted from this comment — which is
 * `lessons.md`'s rule about a comment claiming two numbers are far apart.
 */
const RENDERER_FACT_X = 76;

/**
 * The one line on PREFERENCES that is a **fact rather than a setting**, and the
 * reason it is not a control.
 *
 * A checkbox reading "USE WEBGPU" would be a promise this port cannot keep: the
 * backend is whatever the driver will actually give, and a pilot who ticked the
 * box on a machine whose adapter request fails would tick it, fly, and get WebGL
 * anyway with no explanation. So the panel *reports* — and the escape hatch out
 * of a remembered failure is `?renderer=webgpu`, named in the footer below
 * whenever there is a fallback to explain. `three/renderer-mode.ts`'s module doc
 * has the whole argument.
 */
function rendererFact(facts: RendererFacts | undefined): Widget[] {
  if (!facts) return [];
  return [{ kind: "text", text: rendererFactLabel(facts), x: RENDERER_FACT_X, y: DISPLAY_PLATE.y + 3, role: "label" }];
}

/** A chip naming a field beside a black well holding its current value — the Realism Panel's own selector. */
function selector(chip: Rect, well: Rect, label: string, value: string, action: MenuAction, id: string): Widget[] {
  return [
    { kind: "value", id, rect: chip, text: label, enabled: true, action },
    { kind: "well", rect: well },
    { kind: "wellText", rect: well, text: value },
  ];
}

/**
 * Whether a DISPLAY row can do anything right now — and it is only ever a row
 * with a dependency (`EFFECT_TOGGLE_DEPENDS_ON`) whose parent is switched off,
 * or a row whose backing assets are not being served at all
 * (`enhancedModelsAvailable`).
 *
 * A disabled row keeps its place in `interactiveWidgets`, so the arrow keys
 * still rest on it and the player can read why — the same choice
 * `shell-screen.ts` records for every other disabled control.
 */
function toggleAvailable(id: EffectToggleId, prefs: Preferences, enhancedModelsAvailable: boolean): boolean {
  const parent = EFFECT_TOGGLE_DEPENDS_ON[id];
  if (parent === undefined) return true;
  return prefs.effects[parent] && (parent !== "enhancedModels" || enhancedModelsAvailable);
}

function displaySection(
  prefs: Preferences,
  enhancedModelsAvailable: boolean,
  renderer: RendererFacts | undefined,
): Widget[] {
  const toggles = EFFECT_TOGGLE_IDS.map((id, index): Widget => {
    const y = DISPLAY_ROWS[index] ?? DISPLAY_ROWS[0]!;
    const available = toggleAvailable(id, prefs, enhancedModelsAvailable);
    return {
      kind: "toggle",
      id: `effect-${id}`,
      box: checkbox(18, y),
      plate: { x: 32, y: y - 1, w: 122, h: 13 },
      label: available ? EFFECT_TOGGLE_LABELS[id] : (EFFECT_TOGGLE_UNAVAILABLE_LABELS[id] ?? EFFECT_TOGGLE_LABELS[id]),
      // Still shows the stored bit while unavailable, because the bit is still
      // stored: a pilot who turned it on before switching ENHANCED MODELS off
      // gets it back when he switches them on again.
      checked: prefs.effects[id],
      enabled: available,
      action: command(`pref:effect:${id}`),
    };
  });
  return [
    { kind: "plate", rect: DISPLAY_PLATE },
    sectionHeading(DISPLAY_PLATE, "DISPLAY"),
    ...rendererFact(renderer),
    ...toggles,
  ];
}

/** One bus: its name, a `-` chip, the well printing the percentage, and a `+` chip. */
function volumeRow(bus: (typeof VOLUME_BUSES)[number], y: number, value: number): Widget[] {
  const well: Rect = { x: 227, y: y + 1, w: 36, h: 11 };
  return [
    { kind: "text", text: VOLUME_BUS_LABELS[bus], x: 168, y: y + 3, role: "label" },
    { kind: "value", id: `volume-${bus}-down`, rect: { x: 212, y, w: 13, h: 13 }, text: "-", enabled: true, action: command(`pref:volume:${bus}:down`) },
    { kind: "well", rect: well },
    { kind: "wellText", rect: well, text: volumePercent(value) },
    { kind: "value", id: `volume-${bus}-up`, rect: { x: 265, y, w: 13, h: 13 }, text: "+", enabled: true, action: command(`pref:volume:${bus}:up`) },
  ];
}

function audioSection(prefs: Preferences): Widget[] {
  return [
    { kind: "plate", rect: AUDIO_PLATE },
    sectionHeading(AUDIO_PLATE, "AUDIO"),
    ...VOLUME_BUSES.flatMap((bus, index) => volumeRow(bus, VOLUME_ROWS[index] ?? VOLUME_ROWS[0]!, prefs.audio[bus])),
    {
      kind: "toggle",
      id: "mute",
      box: checkbox(168, MUTE_ROW),
      plate: { x: 182, y: MUTE_ROW - 1, w: 60, h: 13 },
      label: "MUTED",
      checked: prefs.audio.muted,
      enabled: true,
      action: command("pref:mute"),
    },
  ];
}

/**
 * FLYING holds the settings that are neither a port-added picture nor a volume:
 * AD-007n's stick shaping, which is a property of the input device; REALISTIC
 * INSTRUMENTS, which is the Realism Panel's own toggle shown here too because it
 * is the one panel setting a pilot changes mid-sortie (`mission/preferences.ts`
 * keeps the two copies of it in step); and AD-021's BAIL OUT.
 *
 * **BAIL OUT and ENHANCED SIMULATION are here and not in DISPLAY**, and the
 * reason is worth keeping at the line that draws them: DISPLAY's seven rows are
 * port-added *pictures* — things nothing depends on, which a player turns off
 * to get the 1990 look. A bail-out is a gameplay verb that decides whether a
 * career ends, and ENHANCED SIMULATION puts a warship's anti-aircraft battery
 * in the sortie. Filing either under DISPLAY would have said it was cosmetic,
 * and would have let a pilot chasing the 1990 picture silently disarm the North
 * Sea. `flight/bail-settings.ts` and `flight/sim-settings.ts` have the full
 * arguments; the second also has the reason its row is the only one on this
 * panel that is **off** by default.
 */
function flyingSection(prefs: Preferences): Widget[] {
  return [
    { kind: "plate", rect: FLYING_PLATE },
    sectionHeading(FLYING_PLATE, "FLYING"),
    ...selector(
      { x: 168, y: STICK_ROW, w: 44, h: FLYING_ROW_HEIGHT },
      { x: 214, y: STICK_ROW, w: 66, h: FLYING_ROW_HEIGHT },
      "STICK",
      prefs.stick.shaping === "authentic" ? "AUTHENTIC" : "INSTANT",
      command("pref:stick"),
      "stick",
    ),
    {
      kind: "toggle",
      id: "instruments",
      box: checkbox(168, INSTRUMENTS_ROW),
      plate: { x: 182, y: INSTRUMENTS_ROW, w: 118, h: FLYING_ROW_HEIGHT },
      label: "REALISTIC INSTRUMENTS",
      checked: prefs.cockpit.realisticInstruments,
      enabled: true,
      action: command("pref:instruments"),
    },
    {
      kind: "toggle",
      id: "bail",
      box: checkbox(168, BAIL_ROW),
      plate: { x: 182, y: BAIL_ROW, w: 118, h: FLYING_ROW_HEIGHT },
      label: "BAIL OUT",
      checked: prefs.bail.enabled,
      enabled: true,
      action: command("pref:bail"),
    },
    {
      kind: "toggle",
      id: "simulation",
      box: checkbox(168, SIMULATION_ROW),
      plate: { x: 182, y: SIMULATION_ROW, w: 118, h: FLYING_ROW_HEIGHT },
      label: "ENHANCED SIMULATION",
      checked: prefs.simulation.enhancedSimulation,
      enabled: true,
      action: command("pref:simulation"),
    },
  ];
}

export interface PreferencesScreenOptions {
  readonly prefs: Preferences;
  /** Open over a flight rather than from the menus — changes the footer line, nothing else. */
  readonly inFlight?: boolean;
  /**
   * Whether any enhanced models are actually being served
   * (`three/enhanced-catalog.ts`'s index). Only the rows that depend on them
   * read it, and it defaults **true** so the pure model — and every test of it —
   * describes the ordinary install rather than the empty one. `undefined` from
   * the catalog means "the manifest fetch has not landed yet", which must not
   * flash `UNAVAILABLE` at a pilot who has the models: the surface resolves that
   * to `true` and only a *resolved* empty index reaches here as `false`.
   */
  readonly enhancedModelsAvailable?: boolean;
  /**
   * Which backend the next flight will run on and why
   * (`three/renderer-plan.ts`). Omitted, the line is simply absent — which is
   * what every pure test of this screen that does not care about renderers
   * gets, and what an SSR pass with no `navigator` would get.
   */
  readonly renderer?: RendererFacts;
}

/** The key that opens this panel over a flight, printed on the panel so it is discoverable from the menus too. */
export const PREFERENCES_HINT = "PRESS O WHILE FLYING TO OPEN THIS PANEL";
const IN_FLIGHT_HINT = "CHANGES APPLY AT ONCE - O OR ESC RETURNS TO THE FLIGHT";
/**
 * The footer when the port is on a backend nobody asked for.
 *
 * **It beats both other hints**, which is a deliberate trade: the two they
 * carry are a keyboard shortcut and a reminder that edits are live, and both are
 * recoverable by pressing something. A pilot pinned to WebGL by a driver failure
 * recorded on some earlier boot has *no* way back without being told the string,
 * because this port will not re-probe a driver it has already watched fail.
 *
 * Measured, not estimated, and the first draft of this comment had it backwards:
 * 279 px of `FONT3` from x 19, ending at **298** in a 320-wide frame. That is
 * four pixels *wider* than the in-flight hint it replaces (275), not narrower —
 * so this is the longest line the footer has ever carried and it clears the
 * right edge by 22 px. `preferences-renderer-line.test.ts` measures all three
 * against the baked atlas, which is how the mistake was caught.
 */
export const RENDERER_FALLBACK_HINT = "RUNNING ON WEBGL - ADD ?RENDERER=WEBGPU TO RETRY WEBGPU";

function footerHint(options: PreferencesScreenOptions): string {
  if (options.renderer && isRendererFallback(options.renderer)) return RENDERER_FALLBACK_HINT;
  return options.inFlight ? IN_FLIGHT_HINT : PREFERENCES_HINT;
}

export function preferencesScreen(options: PreferencesScreenOptions): ShellScreen {
  const { prefs } = options;
  return {
    id: "preferences",
    chrome: { kind: "panel", title: "PREFERENCES", emblem: "none", background: undefined },
    widgets: [
      { kind: "plate", rect: PREFERENCES_OUTER_PLATE },
      ...displaySection(prefs, options.enhancedModelsAvailable ?? true, options.renderer),
      ...audioSection(prefs),
      ...flyingSection(prefs),
      { kind: "button", id: "realism", rect: REALISM_BUTTON_RECT, label: "REALISM", enabled: true, action: command("pref:realism") },
      { kind: "button", id: "controls", rect: CONTROLS_BUTTON_RECT, label: "CONTROLS", enabled: true, action: command("pref:controls") },
      { kind: "button", id: "restore", rect: RESTORE_BUTTON_RECT, label: "RESTORE", enabled: true, action: command("pref:restore") },
      { kind: "button", id: "accept", rect: ACCEPT_BUTTON_RECT, label: "ACCEPT", enabled: true, action: command("pref:accept") },
      { kind: "text", text: footerHint(options), x: 19, y: 182, role: "label" },
    ],
    cancel: command("pref:accept"),
  };
}
